import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import styles from "./UpdatesFilters.module.scss";
import { RANGE_KEYS, RANGE_OPTIONS } from "./updatesFilterUtils";
import CalendarIcon from "../../assets/icons/Calender.svg?react";

const formatCustomLabel = (from, to) => {
  if (!from || !to) return "Custom Range";
  const fmt = (d) =>
    d.toLocaleDateString("default", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt(from)} – ${fmt(to)}`;
};

const getRangeLabel = (filters) => {
  if (filters.range === RANGE_KEYS.CUSTOM) {
    return formatCustomLabel(filters.from, filters.to);
  }
  const opt = RANGE_OPTIONS.find((o) => o.key === filters.range);
  return opt ? opt.label : "All Time";
};

const useClickOutside = (ref, onOutside) => {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onOutside]);
};

export const Dropdown = ({ buttonLabel, ariaLabel, children, isOpen, setOpen }) => {
  const wrapRef = useRef(null);
  useClickOutside(wrapRef, () => {
    if (isOpen) setOpen(false);
  });

  return (
    <div className={styles.dropdownWrap} ref={wrapRef}>
      <button
        type="button"
        className={classNames("size-14 regular", styles.dropdownButton)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setOpen(!isOpen)}
      >
        <span>{buttonLabel}</span>
        <i className={classNames("tg-icons-ChevronDown", styles.caretIcon)}></i>
      </button>
      {isOpen ? (
        <div className={styles.menu} role="listbox">
          {children}
        </div>
      ) : null}
    </div>
  );
};

const DateRangeFilter = ({ filters, onChange, onOpenCustomRange }) => {
  const [open, setOpen] = useState(false);

  const setRange = (key) => {
    setOpen(false);
    if (key === RANGE_KEYS.CUSTOM) {
      onOpenCustomRange?.();
      return;
    }
    onChange({ ...filters, range: key, from: null, to: null });
  };

  return (
    <Dropdown
      ariaLabel="Filter by date range"
      buttonLabel={
        <>
          <CalendarIcon className={styles.rangeIcon} aria-hidden="true" />
          {getRangeLabel(filters)}
        </>
      }
      isOpen={open}
      setOpen={setOpen}
    >
      {RANGE_OPTIONS.map((opt) => {
        const active = filters.range === opt.key;
        return (
          <div
            key={opt.key}
            role="option"
            aria-selected={active}
            className={classNames(
              "size-14 regular",
              styles.menuItem,
              active && styles.menuItemActive,
            )}
            onClick={() => setRange(opt.key)}
          >
            {opt.label}
          </div>
        );
      })}
    </Dropdown>
  );
};

export default DateRangeFilter;
